import { useMemo, useState } from "react";
import { AppLayout } from "../../components/layout/Header";
import {
  listBetaFeedback,
  listBetaTesters,
  taggedMessages,
  updateBetaFeedback,
} from "../../js/api/betaFeedback";
import { readSupportQueue } from "../../js/api/supportMail";
import { formatDate } from "../../js/utils/formatters";
import { toTitleCase } from "../../js/utils/titleCase";

export default function AdminBeta() {
  const [pulse, setPulse] = useState(0);
  const testers = useMemo(() => listBetaTesters(), [pulse]);
  const rows = useMemo(() => listBetaFeedback(), [pulse]);
  const tagged = useMemo(() => taggedMessages(), [pulse]);
  const supportNotes = useMemo(() => readSupportQueue().slice().reverse(), [pulse]);
  const [notesDraft, setNotesDraft] = useState({});

  function saveNotes(id) {
    updateBetaFeedback(id, {
      admin_notes: notesDraft[id] ?? rows.find((item) => item.id === id)?.admin_notes,
    });
    setPulse((n) => n + 1);
  }

  return (
    <AppLayout variant="admin">
      <div className="page-header">
        <div>
          <h1>Beta feedback</h1>
          <p className="page-lead">
            Confidential early-access testers, program notes, and support mail held on this device.
            This inbox is for the program desk — not a second public product.
          </p>
        </div>
      </div>

      <section className="desk-panel card">
        <h2>Testers</h2>
        {testers.length ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Email</th>
                <th>Accepted</th>
              </tr>
            </thead>
            <tbody>
              {testers.map((user) => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{toTitleCase(user.betaRole || user.role_type)}</td>
                  <td>{user.email}</td>
                  <td>{user.betaAcceptedAt ? formatDate(user.betaAcceptedAt) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">No Beta profiles on this device yet.</p>
        )}
      </section>

      <section className="desk-panel card">
        <h2>Program notes</h2>
        <p className="muted">
          {rows.length} stored note{rows.length === 1 ? "" : "s"}. {tagged.length} message
          {tagged.length === 1 ? "" : "s"} marked as program feedback.
        </p>
        <ul className="admin-suggest-list">
          {rows.map((item) => (
            <li key={item.id} className="card admin-suggest-item">
              <p className="muted">
                {item.from_name || "Beta tester"}
                {item.from_role ? ` · ${toTitleCase(item.from_role)}` : ""} · program feedback ·{" "}
                {formatDate(item.created_at)}
              </p>
              <p>{item.message_text}</p>
              <label className="field">
                <span className="field-label">Admin notes</span>
                <textarea
                  className="field-textarea"
                  rows={3}
                  value={notesDraft[item.id] ?? item.admin_notes ?? ""}
                  onChange={(event) =>
                    setNotesDraft((current) => ({ ...current, [item.id]: event.target.value }))
                  }
                />
              </label>
              <button type="button" className="primary-btn" onClick={() => saveNotes(item.id)}>
                Save notes
              </button>
            </li>
          ))}
          {!rows.length ? <li className="muted">No program notes yet.</li> : null}
        </ul>
      </section>

      <section className="desk-panel card">
        <h2>Support mail</h2>
        <p className="muted">
          Notes routed to sysadmin, support, product, billing, payments, or admin. Held locally
          until a mail server is connected.
        </p>
        <ul className="admin-suggest-list">
          {supportNotes.map((item) => (
            <li key={item.id} className="card admin-suggest-item">
              <p className="muted">
                {item.name || "Visitor"} · {item.to} · {formatDate(item.at)}
              </p>
              <p>{item.message}</p>
              {item.technical ? <p className="muted">{item.technical}</p> : null}
            </li>
          ))}
          {!supportNotes.length ? <li className="muted">No support notes on this device yet.</li> : null}
        </ul>
      </section>
    </AppLayout>
  );
}
